import { IPercentageRange, IProductPriceRange } from '../types'
// TODO these types should probably be moved:
import { IVariant, IProduct } from '../components/products-list'

// a product without a price is has no active offers for the best match returned by agolia
// we don't return a best match from our backend, so check if any variant is active
const isActive = (product: IProduct): boolean =>
  Boolean(product.variant?.price) || product.otherVariants.some(v => v.price)

const ascSort = (a: number, b: number): number => a - b

const DISCOUNT_PERCENTAGE_ROUNDING_THRESHOLD = 0.9

const DISCOUNT_PERCENTAGE_ROUNDING_OFFSET =
  -DISCOUNT_PERCENTAGE_ROUNDING_THRESHOLD + 0.5 // eslint-disable-line no-magic-numbers

export type PriceSelector<Variant extends any> = (
  variant: Variant
) => number | undefined

export interface PriceSelectors<Variant extends any> {
  getOriginalPrice: PriceSelector<Variant>
  getDiscountedPrice: PriceSelector<Variant>
  getEffectivePrice: PriceSelector<Variant>
}

export type CalculateVariantsRange<Variant extends any, Range extends any> = (
  variant: Variant[],
  selectors: PriceSelectors<Variant>
) => Range | undefined

const iVariantSelectors: PriceSelectors<IVariant> = {
  getOriginalPrice: variant => variant?.price?.originalPrice,
  getDiscountedPrice: variant => variant?.price?.discountedPrice,
  getEffectivePrice: variant => variant?.price?.effectivePrice,
}

const ascendingPrices = <Variant extends any>(
  variants: Variant[],
  selector: PriceSelector<Variant>
): number[] =>
  variants.flatMap(variant => selector(variant) ?? []).sort(ascSort)

const minMax = <T extends any>(sorted: T[]): { min: T; max: T } => ({
  min: sorted[0],
  max: sorted[sorted.length - 1],
})

function variantsPriceRange<Variant extends any>(
  variants: Variant[],
  { getOriginalPrice }: PriceSelectors<Variant>
): IProductPriceRange | undefined {
  const prices = ascendingPrices(variants, getOriginalPrice)
  return prices.length === 0 ? undefined : minMax(prices)
}

function variantsDiscountedPriceRange(
  variants: IVariant[],
  { getOriginalPrice, getEffectivePrice }: PriceSelectors<IVariant>
): IProductPriceRange | undefined {
  const originalMinMax = minMax(ascendingPrices(variants, getOriginalPrice))
  const effectiveMinMax = minMax(ascendingPrices(variants, getEffectivePrice))
  const minsEqual = originalMinMax.min === effectiveMinMax.min
  const maxsEqual = originalMinMax.max === effectiveMinMax.max
  return minsEqual && maxsEqual ? undefined : effectiveMinMax
}

function variantsDiscountPercentageRange(
  variants: IVariant[],
  { getOriginalPrice, getEffectivePrice }: PriceSelectors<IVariant>
): IPercentageRange | undefined {
  const originalPrices = ascendingPrices(variants, getOriginalPrice)
  const effectivePrices = ascendingPrices(variants, getEffectivePrice)
  const discounts = originalPrices
    .map((price, i) => {
      const priceInCents = price * 100
      const effectivePriceInCents = effectivePrices[i] * 100
      const percentage =
        ((priceInCents - effectivePriceInCents) / priceInCents) * 100
      return Math.round(percentage + DISCOUNT_PERCENTAGE_ROUNDING_OFFSET)
    })
    .sort(ascSort)
  const { min, max } = minMax(discounts)
  return max === 0 ? undefined : { min, max }
}

function calculateProductRange<Range extends any>(
  product: IProduct,
  calculateVariantsRange: CalculateVariantsRange<IVariant, Range>
): Range | undefined {
  if (!isActive(product)) {
    return undefined
  }
  const variants = product.variant
    ? [product.variant, ...product.otherVariants]
    : product.otherVariants
  return calculateVariantsRange(variants, iVariantSelectors)
}

export function priceRange(product: IProduct): IProductPriceRange | undefined {
  return calculateProductRange(product, variantsPriceRange)
}

export function discountedPriceRange(
  product: IProduct
): IProductPriceRange | undefined {
  return calculateProductRange(product, variantsDiscountedPriceRange)
}

export function discountPercentageRange(
  product: IProduct
): IPercentageRange | undefined {
  return calculateProductRange(product, variantsDiscountPercentageRange)
}
